import React from "react";
import moment, { Moment } from "moment";
import styles from "../../../styles.module.scss";
import { Checkbox, DatePicker } from "antd";
import classNames from "classnames";
import Form, { InputField, NewSelectField, RadioField } from "@components/form";
import { Flex } from "@components/ui/Flex";
import { Text } from "@components/ui/Text";
import { outputFormatOptions, realTimeOptions, requestTypeOptions } from "../constant";
import { ShowWhenTrue } from "@helpers/showwhentrue";
import SelectedTagEdit from "./SelectedTagEdit";
import { TooltipTop } from "@components/tooltips";
import { BsInfoCircle } from "react-icons/bs";
import { timezoneOptions } from "@pages/workflow-page/modals/schedule-workflow";

interface HierarchyFormProps {
    activeStep: number;
    setActiveStep: (step: number) => void;
    initialValues: any;
    formSchema: any;
    selectedRowKeys: string[];
    setShowForm: (show: boolean) => void;
    handleOnSubmit: (values: any) => void;
}

const HierarchyReqForm: React.FC<HierarchyFormProps> = ({
    activeStep,
    setActiveStep,
    initialValues,
    formSchema,
    setShowForm,
    selectedRowKeys,
    handleOnSubmit
}) => {
    const selectedTags = initialValues.tags.split(",");
    return (
        <>
            <Form
                initialValues={initialValues}
                onSubmit={handleOnSubmit}
                validationSchema={formSchema}
                enableReinitialize
            >
                {({ _formikprops: { values, errors, touched, setFieldValue } }) => (
                    <>
                        <div className={styles.jsonHierarchyForm}>
                            <div style={{ padding: 16 }}>
                                <InputField
                                    name="requestName"
                                    label="Request Name"
                                    autoComplete="off"
                                    required={true}
                                    className={styles["requestName__field"]}
                                />
                                <span className={'red-star'}>
                                    Tags
                                </span>
                                <div className={styles["tag_container"]}>
                                    <SelectedTagEdit
                                        selectedTags={selectedTags}
                                        setFieldValue={setFieldValue}
                                        selectedRowKeys={selectedRowKeys}
                                    />
                                </div>
                                <div className={styles["requestName__field"]}>
                                    {!!touched.tags && <Text color={"#ff7761"} fontSize={10}>{errors.tags}</Text>}
                                </div>
                                <InputField
                                    name="elementRootPath"
                                    label="Element Root"
                                    autoComplete="off"
                                    className={styles["requestName__field"]}
                                />
                                <InputField
                                    name="elementPath"
                                    label="Element"
                                    autoComplete="off"
                                    className={styles["requestName__field"]}
                                />
                                <Flex alignItems={"center"} className={classNames(styles["checkbox-tag"],styles["requestName__field"])}>
                                    <Checkbox
                                        name="all_descendants"
                                        onChange={(e) => setFieldValue("all_descendants",e.target.checked)}
                                        checked={values.all_descendants}
                                    >
                                    Include Sub-Elements 
                                    </Checkbox>
                                    <TooltipTop overlay={"Includes this element and all nested elements below it in the hierarchy."}><div><BsInfoCircle/></div></TooltipTop>
                                </Flex>
                                <NewSelectField
                                    name="output_format"
                                    label={"Output Format"}
                                    options={outputFormatOptions}
                                    className={styles["requestName__field"]}
                                />
                                <NewSelectField
                                    name="requestType"
                                    label={<Flex gap="6px">Type <span className="red-star" /><TooltipTop overlay={`Choose how data is retrieved from the PI System:
                                    • Raw fetches actual stored values
                                    • Interpolated fills-in values at regular intervals
                                    • Real-time streams current values as they change
                                    `}><div><BsInfoCircle/></div></TooltipTop></Flex>}
                                    options={requestTypeOptions}
                                    // required={true}
                                    className={styles["requestName__field"]}
                                />
                                <ShowWhenTrue show={values.requestType === "real_time"}>
                                    <RadioField
                                        name="realtime_values"
                                        options={realTimeOptions}
                                        // label='Interpolated Values'
                                        required
                                        className={styles["requestName__field"]}
                                    />
                                    <ShowWhenTrue show={values.realtime_values === "live"}>
                                        <InputField
                                            name="period_ms"
                                            label="Period (in ms)"
                                            autoComplete="off"
                                            required={true}
                                            className={styles["requestName__field"]}
                                        />
                                    </ShowWhenTrue>
                                </ShowWhenTrue>

                                <ShowWhenTrue show={values.requestType === "interpolated"}>
                                    <div>
                                        <Text>Sample Values Every  <span className="red-star" /></Text>
                                        <Flex gap="20px">
                                            <NewSelectField
                                                name="frequency"
                                                options={Array.from({ length: 60 }, (_, i) => i + 1).map((i) => ({ label: i.toString(), value: i.toString() }))}
                                                className={styles["requestName__field"]}
                                            />
                                            <NewSelectField
                                                name="frequencyType"
                                                options={[{ label: 'Seconds', value: "seconds" }, { label: "Minutes", value: "minutes" }, { label: "Hours", value: "hours" }]}
                                                className={styles["requestName__field"]}
                                            />
                                        </Flex>
                                    </div>
                                </ShowWhenTrue>
                                <ShowWhenTrue show={values.requestType != "real_time"}>

                                    <div className="split__fields--halfwide">
                                        <Flex className={styles["requestName__field"]} display={"flex"} flexDirection={"column"} justifyContent={"space-between"}>
                                            <label className="inputfield__label">Start Time <span className="red-star" /></label>
                                            <DatePicker
                                                name="start_time_str"
                                                showTime
                                                format={"MM/DD/YYYY HH:mm:ss"}
                                                value={values.start_time_str ? moment.utc(values.start_time_str) : null}
                                                disabledDate={(current: Moment | null) => !!current && current > moment.utc()}
                                                onChange={(date) => {
                                                    setFieldValue("start_time_str", date);
                                                    if (date && values.end_time_str && moment.utc(values.end_time_str).isBefore(date)) {
                                                        setFieldValue("end_time_str", null);
                                                    }
                                                }}
                                            />
                                        </Flex>
                                        <Flex display={"flex"} flexDirection={"column"} justifyContent={"space-between"}>
                                            <label className="inputfield__label">End Time  <span className="red-star" /></label>
                                            <DatePicker
                                                className={styles["requestName__field"]}
                                                name="end_time_str"
                                                showTime
                                                format={"MM/DD/YYYY HH:mm:ss"}
                                                value={values.end_time_str ? moment.utc(values.end_time_str) : null}
                                                disabledDate={(current: Moment | null) => !!current && (current <= moment.utc(values.start_time_str) || current >= moment.utc())}
                                                onChange={(date) => {
                                                    setFieldValue("end_time_str", date);
                                                }}
                                            />
                                        </Flex>
                                    </div>
                                </ShowWhenTrue>
                                <div className="split__fields--halfwide" style={{ marginTop: -10 }}>
                                    {!!touched.start_time_str && <Text color={"#ff7761"} fontSize={10}>{errors.start_time_str}</Text>}
                                    {!!touched.end_time_str && <Text color={"#ff7761"} fontSize={10}>{errors?.end_time_str}</Text>}
                                </div>
                            </div>
                        </div>
                        <div className={styles.hierarchyFormFooter}>
                            <button type="button" className="btn btn-grey btn-md" onClick={() => { setActiveStep(activeStep - 1); setShowForm(false) }}>
                                Back
                            </button>
                            <button type="submit" className="btn btn-yellow btn-md" >
                                Next
                            </button>
                        </div>
                    </>
                )}
            </Form>
        </>
    );
};

export default HierarchyReqForm;
